import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Container,
	Box,
	Flex,
	Text,
	Center,
	Button,
	Table,
	TableContainer,
	Th,
	Td,
	Thead,
	Tbody,
	Tr,
	Tfoot,
	useDisclosure,
	useToast,
	Input,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
} from '@chakra-ui/react';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import { faFileExcel, faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../../constant';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../../store';
import PrintLayout from '../../../components/Print/PrintLayout';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import getOfficeLabel from '../../../utils/getOfficeLabel';
import getDepartmentLabel from '../../../utils/getDepartmentLabel';
import OfficialListFilter from '../../../components/Filter/OfficialListFilter';
import ExportExcelOfficialsPosition from './excel/excelPosition';

const QUERY_EMPLOYEES = gql`
	query getEmployees($offset: Float, $limit: Float, $name: String, $isActive: Boolean, $department: String, $office: String, $examCandidates: Boolean) {
		getEmployees(
			name: $name
			department: $department
			office: $office
			examCandidates: $examCandidates
			isActive: $isActive
			pagerInput: { offset: $offset, limit: $limit }
		) {
			totalRows
			totalPages
			employees {
				id
				department
				office
				govtNumber
				fullName
				fullNameLatin
				gender
				dob
				createdAt
				phoneNumber
				currentWorkingStatus
				firstWorkingPlace
				additionalRoleOnCurrentJob
				active
				pob
				isPobManual
				pobManual
				currentAddress
				isCurrentAddressManual
				currentAddressManual
				email
				degree
				idCardNumber
				idCardExpirationDate
				spouseStatus
				childInformation
				generalEducation
				specialization
				inTheFieldOfPublicFunctions
				inThePrivateSector
				foreignLanguage
				active
				examCandidates
				note
				employmentStatus
				employmentStatusDate
				positionStatuses {
					id
					rank
					position
					dateOfLastRank
					dateOfLastPosition
					attachmentFile
				}
				latestPositionStatus {
					id
					rank
					position
					dateOfLastRank
					dateOfLastPosition
					attachmentFile
				}
			}
		}
	}
`;

const UPDATE_NOTE = gql`
	mutation updateOfficialsPositionNote($id: String!, $note: String) {
		updateOfficialsPositionNote(id: $id, note: $note)
	}
`;

const limit = 9999;

function OfficialsListPosition() {
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	moment.locale(lang === 'kh' ? 'km' : 'en-gb');
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const department = urlParams.get('department');
	const examCandidates = urlParams.get('examCandidates');
	const office = urlParams.get('office');
	const status = urlParams.get('status');
	const cancelRef = useRef();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [username, setUsername] = useState('');
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const toast = useToast();
	const [notes, setNotes] = useState({});
	const [savingNoteId, setSavingNoteId] = useState(null);
	const [selectedEmployeeNote, setSelectedEmployeeNote] = useState(null);
	const [initialNote, setInitialNote] = useState(null);
	const [loadEmployees, { loading, data }] = useLazyQuery(QUERY_EMPLOYEES);
	const [updateEmployeeNote, { loading: loadingUpdateEmployeesNote, data: dataUpdateEmployeesNote }] = useMutation(UPDATE_NOTE);

	const pageNumber = Number(currentPage) || 1;

	const employees = useMemo(() => {
		return data?.getEmployees?.employees || [];
	}, [data]);

	useEffect(() => {
		const note = {};

		employees?.forEach((employee) => {
			note[employee?.id] = employee?.note || '';
		});

		setNotes(note);
		setInitialNote(note);
	}, [employees]);

	useEffect(() => {
		loadEmployees({
			variables: {
				offset: (pageNumber - 1) * limit,
				limit,
				name: username,
				isActive: status === 'active' ? true : status === 'inactive' ? false : null,
				department,
				office,
				examCandidates: examCandidates === 'true',
			},
		});
	}, [loadEmployees, pageNumber, username, status, department, office, examCandidates]);

	function formatDateOrDash(date) {
		if (!date || !moment(date).isValid()) {
			return '-';
		}

		return moment(date).format('DD MMM YYYY');
	}

	function getPositionDate(employee, position) {
		const records = employee?.positionStatuses || [];
		const findPositonDate = records?.find((record) => record?.position === position);

		if (findPositonDate?.dateOfLastPosition) {
			return formatDateOrDash(findPositonDate?.dateOfLastPosition);
		}
	}

	function callbackFilter(e) {
		history.push(`/officials-list-position${e}`);
	}

	const confirmUpdatingNote = async () => {
		const employee = selectedEmployeeNote;

		if (!employee) {
			return;
		}

		try {
			setSavingNoteId(employee?.id);

			await updateEmployeeNote({
				variables: {
					id: employee?.id,
					note: notes?.[employee?.id] || '',
				},
			});

			setInitialNote({
				...initialNote,
				[employee?.id]: notes?.[employee?.id] || '',
			});

			setSelectedEmployeeNote(null);

			toast({
				title: t('Note has been updated successfully'),
				status: 'success',
				duration: 2000,
				position: 'top-right',
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: t('Failed to update note'),
				description: error?.message,
				status: 'error',
				duration: 3000,
				position: 'top-right',
				isClosable: true,
			});
		} finally {
			setSavingNoteId(null);
		}
	};

	return (
		<>
			<PrintLayout>
				<>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<BreadcrumbComponent
							list={[
								{
									name: 'Official Profile',
									path: `/employee/list?page=1`,
								},
								{
									name: 'List',
									path: `#`,
								},
							]}
						/>
						<Center mt="5">
							<Box
								w="100%"
								bg="white"
								borderRadius={c.borderRadius}
								border="1px solid #dbdbdb"
								boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
								pb="32px"
							>
								<Flex
									alignItems="center"
									justifyContent="space-between"
									bg="#FAFAFA"
									p="16px"
									pt="8px"
									pb="8px"
									mb="16px"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex justifyContent="center" alignItems="center">
										<Text fontSize="x-large" fontWeight="bold" mr="4">
											{t('Officials Position List')}
										</Text>
									</Flex>

									<Flex className="hidden-print">
										<Button
											onClick={() => onOpen()}
											leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
											colorScheme={`${department || office || examCandidates ? 'yellow' : 'gray'}`}
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Filter')}
										</Button>
										<ExportExcelOfficialsPosition employees={employees} notes={notes} ml={2} />
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Print')}
										</Button>
									</Flex>
								</Flex>

								<Box p={4} pt={2}>
									<TableContainer maxH="100vh" overflowY="auto">
										<Table variant="striped" colorScheme="green" size="md">
											<Thead position="sticky" top={0} zIndex={2} bg="white">
												<Tr>
													<Th>{t('Number')}</Th>
													<Th align="left" position="sticky" left="0" zIndex={1} bg="white" maxW="500vw">
														{t('Fullname (Khmer)')}
													</Th>
													<Th align="left">{t('Fullname (Latin)')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('Gender')}</Th>
													<Th align="left">{t('Date of birth')}</Th>
													<Th align="left">{t('Date of Employment')}</Th>
													{/* <Th align="left">{t('Date as Statute Officer')}</Th> */}
													<Th align="left">{t('Statute Officer')}</Th>
													<Th align="left">{t('Deputy Head of Office')}</Th>
													<Th align="left">{t('Head of Office')}</Th>
													<Th align="left">{t('Deputy Head of Department')}</Th>
													<Th align="left">{t('Head of Department')}</Th>
													<Th align="left">{t('Deputy Director General')}</Th>
													<Th align="left">{t('Director General')}</Th>
													<Th align="right">{t('Resignation Date')}</Th>
													<Th align="right">{t('ព្រះរាជក្រឹត្យលេខ/អនុក្រឹត្យលេខ/ប្រកាសលេខ/ផ្សេងៗ')}</Th>
												</Tr>
											</Thead>

											<Tbody>
												{loading ? (
													<Tr>
														<Td colSpan={17} textAlign="left">
															{t('Loading')}...
														</Td>
													</Tr>
												) : employees?.length === 0 ? (
													<Tr>
														<Td colSpan={17} textAlign="left">
															{t('No Data')}
														</Td>
													</Tr>
												) : (
													employees?.map((employee, index) => {
														const positionStatuses = employee?.positionStatuses || null;
														const statuedOfficerRecord =
															employee?.latestPositionStatus?.position === '7'
																? employee?.latestPositionStatus
																: positionStatuses?.find((record) => record?.position === '7');

														const statuedOfficerDate = statuedOfficerRecord?.dateOfLastPosition
															? formatDateOrDash(statuedOfficerRecord?.dateOfLastPosition)
															: employee?.currentWorkingStatus?.currentWorkingStatusPosition === '7'
																? formatDateOrDash(employee?.currentWorkingStatus?.currentWorkingStatusDateOfChangePosition)
																: '-';

														const isNoteEdited = (notes?.[employee?.id] || '') !== (initialNote?.[employee?.id] || '');

														return (
															<Tr key={employee?.id}>
																<Td>{index + 1}</Td>
																<Td position="sticky" left="0" zIndex={1} bg="white">
																	{employee?.fullName || '-'}
																</Td>
																<Td>{employee?.fullNameLatin || '-'}</Td>
																<Td>{employee?.department}</Td>
																<Td>{employee?.office}</Td>
																<Td>{employee?.gender ? t(employee?.gender) : '-'}</Td>
																<Td>{employee?.dob ? moment(employee?.dob).format('DD MMM YYYY') : '-'}</Td>
																<Td>{employee?.createdAt ? moment(employee?.createdAt).format('DD MMM YYYY') : '-'}</Td>
																<Td textAlign="center">{statuedOfficerDate ? statuedOfficerDate : '-'}</Td>
																{/* <Td textAlign="center">{getPositionDate(employee, '7')}</Td> */}
																<Td textAlign="center">{getPositionDate(employee, '6')}</Td>
																<Td textAlign="center">{getPositionDate(employee, '5')}</Td>
																<Td textAlign="center">{getPositionDate(employee, '4')}</Td>
																<Td textAlign="center">{getPositionDate(employee, '3')}</Td>
																<Td textAlign="center">{getPositionDate(employee, '2')}</Td>
																<Td textAlign="center">{getPositionDate(employee, '1')}</Td>
																<Td style={{ textAlign: 'right' }}>
																	{employee?.active === false && employee?.employmentStatusDate
																		? moment(employee?.employmentStatusDate).format('DD MMM YYYY')
																		: '-'}
																</Td>
																<Td minW="250px" p={3}>
																	<Flex alignItems="center" gap={2}>
																		<Input
																			size="sm"
																			variant="outline"
																			bg="white"
																			borderColor="gray.400"
																			color="gray.800"
																			borderRadius="md"
																			boxShadow="sm"
																			value={notes?.[employee?.id] || ''}
																			onChange={(e) => {
																				setNotes({
																					...notes,
																					[employee?.id]: e.target.value,
																				});
																			}}
																			placeholder={t('Note')}
																			_placeholder={{
																				color: 'gray.400',
																			}}
																		/>

																		{isNoteEdited && (
																			<Button
																				size="sm"
																				ml={2}
																				minW="50px"
																				colorScheme="blue"
																				borderRadius="md"
																				boxShadow="sm"
																				isLoading={loadingUpdateEmployeesNote}
																				onClick={() => setSelectedEmployeeNote(employee)}
																			>
																				{t('Save')}
																			</Button>
																		)}
																	</Flex>
																</Td>
															</Tr>
														);
													})
												)}
											</Tbody>
											<Tfoot>
												<Tr>
													<Th>{t('Number')}</Th>
													<Th align="left">{t('Fullname (Khmer)')}</Th>
													<Th align="left">{t('Fullname (Latin)')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('Gender')}</Th>
													<Th align="left">{t('Date of birth')}</Th>
													<Th align="left">{t('Date of Employment')}</Th>
													{/* <Th align="left">{t('Date as Statute Officer')}</Th> */}
													<Th align="left">{t('Statute Officer')}</Th>
													<Th align="left">{t('Deputy Head of Office')}</Th>
													<Th align="left">{t('Head of Office')}</Th>
													<Th align="left">{t('Deputy Head of Department')}</Th>
													<Th align="left">{t('Head of Department')}</Th>
													<Th align="left">{t('Deputy Director General')}</Th>
													<Th align="left">{t('Director General')}</Th>
													<Th align="right">{t('Resignation Date')}</Th>
													<Th align="right">{t('ព្រះរាជក្រឹត្យលេខ/អនុក្រឹត្យលេខ/ប្រកាសលេខ/ផ្សេងៗ')}</Th>
												</Tr>
											</Tfoot>
										</Table>
									</TableContainer>
								</Box>
							</Box>
						</Center>
					</Container>

					<AlertDialog isOpen={!!selectedEmployeeNote} leastDestructiveRef={cancelRef} onClose={() => setSelectedEmployeeNote(null)}>
						<AlertDialogOverlay>
							<AlertDialogContent>
								<AlertDialogHeader fontSize="lg" fontWeight="bold">
									{t('Update Note')}
								</AlertDialogHeader>
								<AlertDialogBody fontSize="15px">{t('Are you sure you want to update this note?')}</AlertDialogBody>
								<AlertDialogFooter>
									<Button
										ref={cancelRef}
										onClick={() => {
											setNotes((prev) => ({
												...prev,
												[selectedEmployeeNote.id]: initialNote?.[selectedEmployeeNote.id] || '',
											}));
											setSelectedEmployeeNote(null);
										}}
										isLoading={loadingUpdateEmployeesNote}
									>
										{t('Cancel')}
									</Button>
									<Button colorScheme="green" onClick={confirmUpdatingNote} isLoading={loadingUpdateEmployeesNote} ml={3}>
										{t('Save')}
									</Button>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialogOverlay>
					</AlertDialog>

					<OfficialListFilter
						isOpen={isOpen}
						onClose={onClose}
						callback={callbackFilter}
						department={department}
						office={office}
						examCandidates={examCandidates}
					/>
				</>
			</PrintLayout>
		</>
	);
}

export default OfficialsListPosition;
